import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    ExclamationTriangleIcon,
    PhoneIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    PencilIcon,
    BuildingOfficeIcon,
    UserIcon
} from '@heroicons/react/24/outline';

interface Inquiry {
    _id: string;
    companyName: string;
    contactPerson: string;
    email: string;
    phone: string;
    message: string;
    status: 'pending' | 'contacted' | 'approved' | 'rejected';
    priority: 'low' | 'medium' | 'high';
    source: string;
    createdAt: string;
    updatedAt: string;
    notes?: string;
    assignedTo?: {
        _id: string;
        firstName: string;
        lastName: string;
    };
}

interface InquiryStats {
    total: number;
    pending: number;
    contacted: number;
    approved: number;
    rejected: number;
    highPriority: number;
    mediumPriority: number;
    lowPriority: number;
}

const CRMManagement: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [stats, setStats] = useState<InquiryStats>({
        total: 0,
        pending: 0,
        contacted: 0,
        approved: 0,
        rejected: 0,
        highPriority: 0,
        mediumPriority: 0,
        lowPriority: 0
    });

    useEffect(() => {
        if (user?.role === 'crm') {
            fetchInquiries();
        }
    }, [user]);

    const fetchInquiries = async () => {
        try {
            setLoading(true);
            const response = await api.get('/inquiries');
            const inquiriesData = response.data.data || [];
            setInquiries(inquiriesData);

            // Calculate stats
            const newStats = {
                total: inquiriesData.length,
                pending: inquiriesData.filter((i: Inquiry) => i.status === 'pending').length,
                contacted: inquiriesData.filter((i: Inquiry) => i.status === 'contacted').length,
                approved: inquiriesData.filter((i: Inquiry) => i.status === 'approved').length,
                rejected: inquiriesData.filter((i: Inquiry) => i.status === 'rejected').length,
                highPriority: inquiriesData.filter((i: Inquiry) => i.priority === 'high').length,
                mediumPriority: inquiriesData.filter((i: Inquiry) => i.priority === 'medium').length,
                lowPriority: inquiriesData.filter((i: Inquiry) => i.priority === 'low').length
            };
            setStats(newStats);
        } catch (error) {
            console.error('Error fetching inquiries:', error);
            showToast({ message: 'Error fetching inquiries', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (inquiryId: string, newStatus: string) => {
        try {
            await api.put(`/api/inquiries/${inquiryId}`, { status: newStatus });
            setInquiries(inquiries.map(inquiry =>
                inquiry._id === inquiryId ? { ...inquiry, status: newStatus as any } : inquiry
            ));
            showToast({ message: 'Inquiry status updated successfully', type: 'success' });
            fetchInquiries(); // Refresh stats
        } catch (error) {
            console.error('Error updating inquiry status:', error);
            showToast({ message: 'Error updating inquiry status', type: 'error' });
        }
    };

    const handlePriorityUpdate = async (inquiryId: string, newPriority: string) => {
        try {
            await api.put(`/api/inquiries/${inquiryId}`, { priority: newPriority });
            setInquiries(inquiries.map(inquiry =>
                inquiry._id === inquiryId ? { ...inquiry, priority: newPriority as any } : inquiry
            ));
            showToast({ message: 'Inquiry priority updated successfully', type: 'success' });
            fetchInquiries(); // Refresh stats
        } catch (error) {
            console.error('Error updating inquiry priority:', error);
            showToast({ message: 'Error updating inquiry priority', type: 'error' });
        }
    };

    const handleNotesUpdate = async (inquiryId: string, notes: string) => {
        try {
            await api.put(`/api/inquiries/${inquiryId}`, { notes });
            setInquiries(inquiries.map(inquiry =>
                inquiry._id === inquiryId ? { ...inquiry, notes } : inquiry
            ));
            showToast({ message: 'Notes updated successfully', type: 'success' });
        } catch (error) {
            console.error('Error updating notes:', error);
            showToast({ message: 'Error updating notes', type: 'error' });
        }
    };

    const filteredInquiries = inquiries.filter(inquiry => {
        const matchesSearch =
            inquiry.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inquiry.message.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = filterStatus === 'all' || inquiry.status === filterStatus;
        const matchesPriority = filterPriority === 'all' || inquiry.priority === filterPriority;

        return matchesSearch && matchesStatus && matchesPriority;
    });

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'low': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'contacted': return 'bg-blue-100 text-blue-800';
            case 'approved': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (user?.role !== 'crm') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">CRM Management</h1>
                    <p className="mt-2 text-gray-600">Manage customer inquiries and relationships</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-8 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.pending}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <PhoneIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Contacted</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.contacted}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Approved</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.approved}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <XCircleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Rejected</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.rejected}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">High Priority</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.highPriority}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClockIcon className="h-8 w-8 text-yellow-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Medium</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.mediumPriority}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">Low Priority</dt>
                                    <dd className="text-lg font-medium text-gray-900">{stats.lowPriority}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search inquiries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <select
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">All Priority</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                </div>

                {/* Inquiries List */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Inquiries ({filteredInquiries.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredInquiries.map((inquiry) => (
                                    <tr key={inquiry._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {inquiry.companyName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {inquiry.source}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {inquiry.contactPerson}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {inquiry.email}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {inquiry.phone}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(inquiry.priority)}`}>
                                                {inquiry.priority}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(inquiry.status)}`}>
                                                {inquiry.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(inquiry.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => setSelectedInquiry(inquiry)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => setSelectedInquiry(inquiry)}
                                                className="text-green-600 hover:text-green-900"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Inquiry Details Modal */}
                {selectedInquiry && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        Inquiry Details - {selectedInquiry.companyName}
                                    </h3>
                                    <button
                                        onClick={() => setSelectedInquiry(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <XCircleIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {/* Company Info */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-3">Company Information</h4>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="font-medium">Company:</span>
                                                <span className="ml-2">{selectedInquiry.companyName}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Source:</span>
                                                <span className="ml-2">{selectedInquiry.source}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Contact Person:</span>
                                                <span className="ml-2">{selectedInquiry.contactPerson}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Email:</span>
                                                <span className="ml-2">{selectedInquiry.email}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Phone:</span>
                                                <span className="ml-2">{selectedInquiry.phone}</span>
                                            </div>
                                            <div>
                                                <span className="font-medium">Created:</span>
                                                <span className="ml-2">{new Date(selectedInquiry.createdAt).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2">Message</h4>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <p className="text-gray-700">{selectedInquiry.message}</p>
                                        </div>
                                    </div>

                                    {/* Status and Priority */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                            <select
                                                value={selectedInquiry.status}
                                                onChange={(e) => handleStatusUpdate(selectedInquiry._id, e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="contacted">Contacted</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                            <select
                                                value={selectedInquiry.priority}
                                                onChange={(e) => handlePriorityUpdate(selectedInquiry._id, e.target.value)}
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                        <textarea
                                            value={selectedInquiry.notes || ''}
                                            onChange={(e) => setSelectedInquiry({ ...selectedInquiry, notes: e.target.value })}
                                            onBlur={() => handleNotesUpdate(selectedInquiry._id, selectedInquiry.notes || '')}
                                            rows={4}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Add notes about this inquiry..."
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        onClick={() => setSelectedInquiry(null)}
                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CRMManagement;
